import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../lib/useLang";
import { useAuth } from "../state/AuthContext";
import { useCart } from "../state/CartContext";
import { demoStore } from "../lib/demoStore";
import { formatKWD } from "../lib/format";
import { modifierSummary } from "../data/modifiers";
import { AddressBook } from "../components/AddressBook";
import type { Order } from "../lib/types";

export default function AccountPage() {
  const { t, lang, pick } = useLang();
  const { user, signIn, signOut } = useAuth();
  const { add } = useCart();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  // --- Guest: show the optional sign-in form ---
  if (!user) {
    const canSubmit = name.trim() && phone.trim();
    return (
      <div className="mx-auto max-w-md space-y-4">
        <h1 className="display text-2xl text-ink">{t("account.signInTitle")}</h1>
        <p className="text-sm text-black/55">{t("account.signInBody")}</p>
        <div className="card space-y-3 p-4">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-black/55">
              {t("checkout.name")}
            </span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-black/55">
              {t("checkout.phone")}
            </span>
            <input
              className="input"
              inputMode="tel"
              dir="ltr"
              placeholder="+965 …"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <button
            className="btn-primary w-full"
            disabled={!canSubmit}
            onClick={() => canSubmit && signIn(name, phone)}
          >
            {t("account.signIn")}
          </button>
          <p className="text-center text-xs text-black/45">{t("account.guestNote")}</p>
        </div>
      </div>
    );
  }

  // --- Signed in: profile + recorded order history (by account uid or phone) ---
  const orders = demoStore
    .getOrders()
    .filter((o) => o.customerUid === user.uid || o.customer.phone === user.phone);

  function reorder(o: Order) {
    for (const l of o.lines) {
      add(
        {
          itemId: l.itemId,
          type: l.type,
          name: l.name,
          nameAr: l.nameAr,
          price: l.price,
          basePrice: l.basePrice,
          session: l.session,
          fastDelivery: l.fastDelivery,
          imageURL: l.imageURL,
          notes: l.notes,
          modifiers: l.modifiers,
        },
        l.qty,
      );
    }
    navigate("/cart");
  }

  return (
    <div className="mx-auto max-w-md space-y-5">
      <div className="card flex items-center gap-3 p-4">
        <div className="grid size-12 place-items-center rounded-full bg-brand/10 text-xl">👤</div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-bold text-ink">{user.name}</div>
          <div className="text-sm text-black/50" dir="ltr">
            {user.phone}
          </div>
        </div>
        <button className="btn-outline text-sm" onClick={signOut}>
          {t("account.signOut")}
        </button>
      </div>

      <AddressBook />

      <div>
        <h2 className="display mb-2 text-xl text-ink">{t("account.orders")}</h2>
        {orders.length === 0 ? (
          <div className="card p-6 text-center text-sm text-black/50">
            {t("account.noOrders")}
            <div className="mt-3">
              <Link to="/" className="btn-primary">
                {t("cart.browse")}
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="card p-4">
                <div className="flex items-center justify-between">
                  <span className="font-extrabold text-brand" dir="ltr">
                    #{o.id}
                  </span>
                  <span className="chip bg-brand/10 text-brand">
                    {t(`admin.status.${o.orderStatus}`)}
                  </span>
                </div>
                <div className="mt-1 text-xs text-black/45">
                  {o.lines.reduce((s, l) => s + l.qty, 0)} {t("common.items")} ·{" "}
                  {new Date(o.createdAt).toLocaleDateString(lang === "ar" ? "ar-KW" : "en-GB")}
                </div>
                <ul className="mt-2 space-y-0.5 text-sm">
                  {o.lines.map((l, i) => {
                    const mods = modifierSummary(l.modifiers, lang);
                    return (
                      <li key={l.uid ?? `${l.itemId}-${i}`} className="text-black/70">
                        {l.qty}× {pick(l.name, l.nameAr)}
                        {mods ? <span className="text-black/40"> — {mods}</span> : null}
                        {l.notes ? <span className="text-black/40"> — {l.notes}</span> : null}
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-2 border-t border-black/5 pt-2 text-end font-bold">
                  {formatKWD(o.total || o.subtotal, lang)}
                </div>
                <div className="mt-3 flex gap-2">
                  <Link to={`/track/${o.id}`} className="btn-outline flex-1 text-sm">
                    🛵 {t("track.find")}
                  </Link>
                  <button
                    className="btn-primary flex-1 text-sm"
                    onClick={() => reorder(o)}
                  >
                    🔁 {t("account.reorder")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
